import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Hand, Send, Trash2, Pencil, Check, X } from "lucide-react";
import api from "../lib/api";
import Avatar from "../components/Avatar";
import { useAuth } from "../context/AuthContext";
import { formatRange, relativeTime, KANBAN_COLUMNS } from "../lib/format";

function CommentItem({ c, opportunityId, currentUser, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.text);
  const canModify = c.author_id === currentUser.id || currentUser.role === "admin";

  const save = async () => {
    if (!draft.trim()) return;
    const { data } = await api.patch(`/opportunities/${opportunityId}/comments/${c.id}`, { text: draft });
    setEditing(false);
    onUpdated(data);
  };
  const onDelete = async () => {
    if (!window.confirm("Delete this comment?")) return;
    const { data } = await api.delete(`/opportunities/${opportunityId}/comments/${c.id}`);
    onUpdated(data);
  };

  return (
    <li className="flex items-start gap-3 group" data-testid={`comment-${c.id}`}>
      <Avatar name={c.author?.name || "?"} photoUrl={c.author?.photo_url} color={c.author?.avatar_color || "#C9A84C"} size={32} />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-[#9A9590] flex items-center gap-2">
          <span className="text-[#F0EDE8]">{c.author?.name || "Member"}</span>
          <span>·</span>
          <span>{relativeTime(c.created_at)}</span>
          {c.edited_at && <span className="italic text-[#9A9590]/70">· edited</span>}
        </div>
        {editing ? (
          <div className="mt-1.5 space-y-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              autoFocus
              className="ts-input resize-none py-2"
              data-testid={`comment-edit-input-${c.id}`}
            />
            <div className="flex gap-2">
              <button onClick={save} className="btn-gold rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid={`comment-save-${c.id}`}>
                <Check size={12}/> Save
              </button>
              <button onClick={() => { setEditing(false); setDraft(c.text); }} className="text-xs text-[#9A9590] hover:text-[#F0EDE8] inline-flex items-center gap-1">
                <X size={12}/> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 mt-0.5">
            <div className="text-sm text-[#F0EDE8] leading-relaxed flex-1 whitespace-pre-line">{c.text}</div>
            {canModify && (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 shrink-0">
                <button onClick={() => setEditing(true)} className="text-[#9A9590] hover:text-[#C9A84C]" data-testid={`comment-edit-${c.id}`}>
                  <Pencil size={13} />
                </button>
                <button onClick={onDelete} className="text-[#9A9590] hover:text-red-400" data-testid={`comment-delete-${c.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [opp, setOpp] = useState(null);
  const [comment, setComment] = useState("");

  const load = useCallback(() => api.get(`/opportunities/${id}`).then((r) => setOpp(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  if (!opp) return <div className="text-[#9A9590]">Loading…</div>;

  const toggleInterest = async () => {
    const { data } = await api.post(`/opportunities/${id}/interest`);
    setOpp(data);
  };
  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    const { data } = await api.post(`/opportunities/${id}/comments`, { text: comment });
    setOpp(data);
    setComment("");
  };
  const changeStatus = async (status) => {
    const { data } = await api.patch(`/opportunities/${id}`, { status });
    setOpp(data);
  };
  const onDelete = async () => {
    if (!window.confirm("Archive this opportunity?")) return;
    await api.delete(`/opportunities/${id}`);
    window.location.href = "/opportunities";
  };

  const interested = (opp.interested_member_ids || []).includes(user.id);
  const isPoster = opp.posted_by === user.id;
  const canManage = isPoster || user.role === "admin";

  return (
    <div className="space-y-8" data-testid="opp-detail-page">
      <Link to="/opportunities" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#9A9590] hover:text-[#C9A84C] transition">
        <ArrowLeft size={14} /> Back to pipeline
      </Link>

      <div className="ts-card p-8 sm:p-10">
        <div className="overline">{opp.client_type || "Opportunity"}</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#F0EDE8] mt-3 tracking-tight">{opp.title}</h1>
        <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-[#9A9590]">
          <span>Posted by <span className="text-[#F0EDE8]">{opp.posted_by_user?.name || "—"}</span></span>
          <span>·</span>
          <span>{relativeTime(opp.created_at)}</span>
          <span>·</span>
          <span className="font-serif text-[#F0EDE8] text-lg">{formatRange(opp.estimated_value_min, opp.estimated_value_max)}</span>
        </div>

        <div className="flex flex-wrap gap-2 mt-5">
          {(opp.sectors_involved || []).map((s) => (
            <span key={s} className="text-[10px] uppercase tracking-widest text-[#C9A84C] border border-[#C9A84C]/30 rounded-full px-3 py-1">
              {s}
            </span>
          ))}
        </div>

        <p className="mt-6 text-[#F0EDE8] leading-relaxed whitespace-pre-line">{opp.description || "—"}</p>

        {opp.skills_needed?.length > 0 && (
          <div className="mt-6">
            <div className="overline mb-2">Skills needed</div>
            <div className="flex flex-wrap gap-2">
              {opp.skills_needed.map((s) => (
                <span key={s} className="text-xs text-[#9A9590] border border-[#C9A84C]/15 rounded-full px-3 py-1">
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            onClick={toggleInterest}
            data-testid="express-interest"
            className={`px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm transition ${
              interested ? "btn-gold" : "btn-ghost-gold"
            }`}
          >
            <Hand size={15} />
            {interested ? "Interested ✓" : "I can contribute"}
          </button>
          {canManage && (
            <>
              <div className="flex items-center gap-2 ml-2">
                <span className="overline">Status</span>
                <select
                  value={opp.status}
                  onChange={(e) => changeStatus(e.target.value)}
                  className="bg-[#1A1A1A] border border-[#C9A84C]/25 rounded-md px-3 py-2 text-sm text-[#F0EDE8] focus:outline-none focus:border-[#C9A84C]"
                  data-testid="opp-status-select"
                >
                  {KANBAN_COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  <option value="on_hold">On Hold</option>
                </select>
              </div>
              {user.role === "admin" && (
                <button onClick={onDelete} className="ml-auto text-xs text-[#9A9590] hover:text-red-400 inline-flex items-center gap-1" data-testid="opp-delete">
                  <Trash2 size={14} /> Archive
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="ts-card p-7">
          <div className="overline mb-3">Interested members ({opp.interested_members?.length || 0})</div>
          <ul className="space-y-3" data-testid="interested-list">
            {(opp.interested_members || []).map((m) => (
              <li key={m.id} className="flex items-center gap-3">
                <Avatar name={m.name} photoUrl={m.photo_url} color={m.avatar_color} size={36} />
                <div>
                  <div className="text-sm text-[#F0EDE8]">{m.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#C9A84C]">{m.sector}</div>
                </div>
              </li>
            ))}
            {(!opp.interested_members || opp.interested_members.length === 0) && (
              <li className="text-sm text-[#9A9590]">No one yet. Be the first.</li>
            )}
          </ul>
        </div>

        <div className="ts-card p-7">
          <div className="overline mb-3">Conversation ({opp.comments?.length || 0})</div>
          <ul className="space-y-4 mb-4 max-h-[320px] overflow-y-auto pr-2" data-testid="comments-list">
            {(opp.comments || []).map((c) => (
              <CommentItem key={c.id} c={c} opportunityId={id} currentUser={user} onUpdated={setOpp} />
            ))}
            {(!opp.comments || opp.comments.length === 0) && (
              <li className="text-sm text-[#9A9590]">No comments yet.</li>
            )}
          </ul>
          <form onSubmit={submitComment} className="flex items-end gap-2 border-t border-[#C9A84C]/12 pt-3" data-testid="comment-form">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a note for the group…"
              rows={2}
              className="ts-input flex-1 resize-none"
              data-testid="comment-input"
            />
            <button type="submit" className="btn-gold rounded-md px-4 py-2.5 inline-flex items-center gap-2 text-sm">
              <Send size={14} /> Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
